import type { WritableSignal } from '@angular/core';
import { inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';

import type { AppIdentity } from '@evc/platform';
import { BtnActionEnum, type Menu, type MenuItemType } from '@evc/web-components';
import { I18nService } from '@shared/services/i18n/i18n.service';
import { VERSION } from '@shared/version/version';

@Injectable({
  providedIn: 'root',
})
export class NavConfigService {
  readonly #router = inject(Router);
  readonly #i18nService = inject(I18nService);

  // created on #initConfig()
  #leftbarConfig!: WritableSignal<Menu[]>;
  #appIdentity!: WritableSignal<AppIdentity>;
  #helpMenu!: WritableSignal<MenuItemType[]>;

  constructor() {
    this.#initConfig();

    this.#router.events.pipe(takeUntilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.updateSelectedMenuItem();
      }
    });
  }

  #initConfig(): void {
    this.#leftbarConfig = signal<Menu[]>([]);

    this.#appIdentity = signal<AppIdentity>({
      title: this.#i18nService.t$('title'),
      logo: '../assets/applications/equipment-manager.svg#equipment-manager',
      version: VERSION.semver,
    });

    this.#helpMenu = signal<MenuItemType[]>(
      ['help_and_support', 'release_notes', 'terms_of_use', 'platform_status'].map((key) => ({
        key,
        text: this.#i18nService.t$(`links.${key}.text`),
        action: {
          type: BtnActionEnum.URL,
          callbackOrUrl: this.#i18nService.t$(`links.${key}.href`),
        },
      })) as MenuItemType[],
    );
  }

  updateSelectedMenuItem(): void {
    if (!this.#leftbarConfig) return;

    const currentUrl = this.#router.url;

    this.#leftbarConfig.update((config) =>
      config.map((section) => ({
        ...section,
        items: section.items?.map((item) => ({
          ...item,
          selected: item.key === currentUrl,
        })),
      })),
    );
  }

  public get leftbarConfig(): WritableSignal<Menu[]> {
    return this.#leftbarConfig;
  }

  public set leftbarConfig(value: Menu[]) {
    this.#leftbarConfig.set(value);
  }

  public get appIdentity(): WritableSignal<AppIdentity> {
    return this.#appIdentity;
  }

  public get helpMenu(): WritableSignal<MenuItemType[]> {
    return this.#helpMenu;
  }
}
